import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AccountSettings.css';
import { useUser } from '../../contexts/UserContext';
import { API_BASE_URL } from '../../config';
import DynamicBackground from '../../components/DynamicBackground';
import '../../styles/darkMode.css';
import { COUNTRIES, US_STATES, CANADIAN_PROVINCES, MUSIC_INDUSTRY_ROLES } from '../../constants/countries-states';


const AccountSettings: React.FC = () => {
  const { user, setUser } = useUser();
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Pre-populate form with user data
  useEffect(() => {
    if (user) {
      setCountry(user.country || '');
      setState(user.state || '');
      setRole(user.role || '');
      setCompany(user.company || '');
    }
  }, [user]);

  // Redirect logic: ProtectedRoute ensures user is authenticated
  // So we only need to check onboarding status, not authentication
  useEffect(() => {
    // Wait for user to load from context
    if (user === null) {
      // User is still loading, don't redirect yet
      // ProtectedRoute will handle unauthenticated users
      return;
    }
    
    // Only redirect to onboarding if user hasn't completed onboarding yet
    // Onboarded users can always access this page - NO redirect
    if (!user.onboarded) {
      navigate('/onboarding', { replace: true });
      return;
    }
    // If user is onboarded, they stay on this page (no redirect)
  }, [user, navigate]);

  // Reset state when country changes
  useEffect(() => {
    if (country !== 'US' && country !== 'CA') {
      setState('');
    }
  }, [country]);

  const getStateOptions = () => {
    if (country === 'US') {
      return US_STATES;
    } else if (country === 'CA') {
      return CANADIAN_PROVINCES;
    }
    return [];
  };

  const isStateRequired = country === 'US' || country === 'CA';

  // Show loading state while user is loading or if not onboarded
  // Onboarded users will see the form
  if (user === null) {
    // User is still loading from context
    return (
      <DynamicBackground>
        <div className="dark-container">
          <div className="account-settings-header">
            <h2 className="dark-title">Account Settings</h2>
            <p className="dark-subtitle">Loading...</p>
          </div>
        </div>
      </DynamicBackground>
    );
  }

  if (!user.onboarded) {
    // Will be redirected by useEffect, but show loading state
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      const payload: any = {
        id: user.id,
      };

      // Always include country if provided
      if (country) {
        payload.country = country;
      }
      
      // Handle state: include if required and has value, or clear if not required
      if (isStateRequired && state) {
        payload.state = state;
      } else if (!isStateRequired) {
        // Clear state when switching from US/CA to another country
        payload.state = null;
      }
      
      // Always include role if provided
      if (role) {
        payload.role = role;
      }
      
      // Handle company: include if has value, or clear if empty
      if (company && company.trim()) {
        payload.company = company.trim();
      } else {
        // Allow clearing company by sending null
        payload.company = null;
      }

      const res = await fetch(`${API_BASE_URL}/api/users/${user.id}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.idToken}`
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update profile');
      }
      
      const updatedUser = await res.json();
      
      // Update user context with new data, preserving credits and other fields
      setUser({ 
        ...user, 
        country: updatedUser.country, 
        state: updatedUser.state, 
        role: updatedUser.role, 
        company: updatedUser.company,
        // Explicitly preserve credits and other important fields
        credits: user.credits,
        subscriptionStatus: user.subscriptionStatus,
        subscriptionId: user.subscriptionId,
        stripeCustomerId: user.stripeCustomerId,
        subscriptionEndDate: user.subscriptionEndDate,
      });
      
      setSuccess(true);
      // Redirect after a short delay to show success message
      setTimeout(() => {
        navigate('/tools');
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DynamicBackground>
      <div className="dark-container">
        <div className="account-settings-header">
          <h2 className="dark-title">Account Settings</h2>
          <p className="dark-subtitle">Update your profile information</p>
        </div>
        
        <form className="account-settings-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="country" className="form-label">
              Country <span className="required-asterisk">*</span>
            </label>
            <select
              id="country"
              className="dark-select"
              value={country}
              onChange={e => setCountry(e.target.value)}
              required
            >
              {COUNTRIES.map(countryOption => (
                <option key={countryOption.value} value={countryOption.value}>
                  {countryOption.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="state" className="form-label">
              {country === 'CA' ? 'Province/Territory' : 'State'}
              {isStateRequired && <span className="required-asterisk">*</span>}
            </label>
            <select
              id="state"
              className="dark-select"
              value={state}
              onChange={e => setState(e.target.value)}
              disabled={!isStateRequired}
              required={isStateRequired}
            >
              {isStateRequired ? (
                getStateOptions().map(stateOption => (
                  <option key={stateOption.value} value={stateOption.value}>
                    {stateOption.label}
                  </option>
                ))
              ) : (
                <option value="">Not applicable</option>
              )}
            </select>
            {!isStateRequired && (
              <span className="field-hint">State/Province selection is only available for USA and Canada</span>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="role" className="form-label">
              Role in music industry <span className="required-asterisk">*</span>
            </label>
            <select
              id="role"
              className="dark-select"
              value={role}
              onChange={e => setRole(e.target.value)}
              required
            >
              {MUSIC_INDUSTRY_ROLES.map(roleOption => (
                <option key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="company" className="form-label">
              Company name <span className="optional-label">(optional)</span>
            </label>
            <input
              id="company"
              type="text"
              className="dark-input"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="Enter your company name"
            />
          </div>
          
          {error && <div className="account-settings-error">{error}</div>}
          {success && <div className="account-settings-success">Profile updated successfully! Redirecting...</div>}
          
          <button 
            type="submit" 
            className="dark-button primary account-settings-submit-btn"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'SAVE CHANGES'}
          </button>
        </form>
      </div>
    </DynamicBackground>
  );
};

export default AccountSettings;
